import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useConfirmation } from '@/components/shared/ZCard';
import { Plus, X, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZLoadingBar from '@/components/shared/ZLoadingBar';

const CHRONICLE_COLOR = '#C04040';

const TYPE_LABELS = {
  witnessed: 'Witnessed',
  relational: 'Relational',
  self:       'Self',
  place:      'Place',
  conflict:   'Conflict',
};

const TYPE_COLORS = {
  witnessed: '#7B9EA8',
  relational: '#A87B9E',
  self:       '#9EA87B',
  place:      '#7B8FA8',
  conflict:   '#A88A7B',
};

function formatEntryDate(dateStr) {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }
  return dateStr;
}

function renderBody(body) {
  const parts = body.split(/(\*\*[^*]+:\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+:)\*\*$/);
    if (m) return <span key={i} style={{ color: '#b0b4c0', fontWeight: 500, fontFamily: 'monospace', fontSize: 12 }}>{m[1]}{' '}</span>;
    return <span key={i}>{part}</span>;
  });
}

// ── New Chronicle Modal ────────────────────────────────────────────────────────

function NewChronicleModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [type, setType] = useState('self');
  const [saving, setSaving] = useState(false);
  const feedback = useConfirmation();

  const handleCreate = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/chronicle/chronicles', { title, subject, type });
      feedback.confirm();
      onCreated(data);
      onClose();
    } catch { feedback.fail(); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: CHRONICLE_COLOR }}>New Chronicle</span>
          <button onClick={onClose} style={{ color: '#6b7084', background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ marginBottom: 14 }}>
          <p style={labelStyle}>Title</p>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Children — Quinn & Xavier" style={inputStyle} autoFocus />
        </div>
        <div style={{ marginBottom: 14 }}>
          <p style={labelStyle}>Subject</p>
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Quinn and Xavier" style={inputStyle} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <p style={labelStyle}>Type</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.entries(TYPE_LABELS).map(([t, label]) => (
              <button key={t} onClick={() => setType(t)} style={{
                background: type === t ? `${TYPE_COLORS[t]}22` : 'transparent',
                color: type === t ? TYPE_COLORS[t] : '#6b7084',
                border: `0.5px solid ${type === t ? `${TYPE_COLORS[t]}66` : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 20, padding: '4px 12px', fontSize: 11,
                fontFamily: 'monospace', cursor: 'pointer',
              }}>{label}</button>
            ))}
          </div>
        </div>
        <button onClick={handleCreate} disabled={saving} style={primaryBtnStyle}>
          {saving ? 'Creating…' : 'Create Chronicle'}
        </button>
      </div>
    </div>
  );
}

// ── Add Entry Sheet ────────────────────────────────────────────────────────────

function AddEntrySheet({ chronicle, onClose, onAdded }) {
  const [body, setBody] = useState('');
  const [context, setContext] = useState('');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);
  const feedback = useConfirmation();

  useEffect(() => { setTimeout(() => textareaRef.current?.focus(), 50); }, []);

  const handleSave = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/chronicle/chronicles/${chronicle.id}/entries`, {
        body, context: context || undefined, source: 'manual',
      });
      feedback.confirm();
      onAdded(data);
      onClose();
    } catch { feedback.fail(); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 55 }} />
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 60, padding: '0 0 8px' }}>
        <div style={{ width: '70%', maxWidth: 560, margin: '0 auto' }}>
          <div style={{ background: 'rgba(255,255,255,0.05)', border: `2px solid ${CHRONICLE_COLOR}cc`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px 4px' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 10, color: CHRONICLE_COLOR, letterSpacing: '0.12em', textTransform: 'uppercase', flex: 1 }}>
                Add to {chronicle.title}
              </span>
              <button onClick={onClose} style={{ color: '#6b7084', background: 'none', border: 'none', cursor: 'pointer' }}><X size={14} /></button>
            </div>
            <div style={{ padding: '4px 16px 12px' }}>
              <textarea
                ref={textareaRef}
                value={body}
                rows={1}
                onChange={e => { setBody(e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSave(); } }}
                placeholder="The entry itself…"
                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#f0f0f5', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 14, lineHeight: 1.8, resize: 'none', maxHeight: '40vh' }}
              />
              <input
                value={context}
                onChange={e => setContext(e.target.value)}
                placeholder="Context: place, time (optional)"
                style={{ width: '100%', background: 'transparent', border: 'none', borderTop: '0.5px solid rgba(255,255,255,0.04)', outline: 'none', color: '#6b7084', fontFamily: 'monospace', fontSize: 10, padding: '6px 0 0', letterSpacing: '0.06em' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={handleSave} disabled={saving || !body.trim()} style={{ ...primaryBtnStyle, width: 'auto', padding: '6px 18px', fontSize: 11 }}>
                  {saving ? 'Saving…' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Import Bar ─────────────────────────────────────────────────────────────────
// Phase 1 (analyse): paste → Claude segments + detects Chronicle + flags splits
// Phase 2 (apply):   Werner reviews proposal, confirms → single DB write

function ImportBar({ chronicles = [], onDone, onChronicleCreated, hasSidebar }) {
  const feedback = useConfirmation();
  const [open, setOpen]               = useState(false);
  const [step, setStep]               = useState('form'); // form | review
  const [sourceLabel, setSourceLabel] = useState('');
  const [importMode, setImportMode]   = useState('append');
  const [rawText, setRawText]         = useState('');
  const [loading, setLoading]         = useState(false);
  const [proposal, setProposal]       = useState(null);

  // Chronicle resolution (pre-populated from Claude, editable by Werner)
  const [confirmedChronicleId, setConfirmedChronicleId] = useState(null);
  const [createForm, setCreateForm]   = useState({ title: '', type: 'relational', subject: '' });
  const [useCreate, setUseCreate]     = useState(false);
  const [pickingExisting, setPickingExisting] = useState(false);

  // Split assignments: entry_index → chronicle_id; dismissed = stays in primary
  const [splitAssignments, setSplitAssignments] = useState({});
  const [dismissedSplits, setDismissedSplits]   = useState(new Set());

  // Original body toggle: index → true means showing raw pre-rewrite original
  const [showOriginal, setShowOriginal] = useState({});
  const toggleShowOriginal = (i) => setShowOriginal(prev => ({ ...prev, [i]: !prev[i] }));

  const textareaRef  = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || !open) return;
    const update = () => {
      if (containerRef.current) {
        const off = window.innerHeight - vv.height - vv.offsetTop;
        containerRef.current.style.bottom = `${Math.max(0, off)}px`;
      }
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setStep('form');
    setTimeout(() => textareaRef.current?.focus(), 80);
  };

  const reset = () => {
    setOpen(false);
    setStep('form');
    setRawText('');
    setSourceLabel('');
    setProposal(null);
    setConfirmedChronicleId(null);
    setUseCreate(false);
    setPickingExisting(false);
    setSplitAssignments({});
    setDismissedSplits(new Set());
  };

  // ── Phase 1: paste → Claude analyses ──────────────────────────────────────
  const handleAnalyse = async () => {
    if (!rawText.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/chronicle/import/preview', {
        source_label: sourceLabel,
        import_mode: importMode,
        raw_text: rawText,
      });
      setProposal(data);
      // Pre-populate from Claude's analysis
      if (data.primary_chronicle?.id) {
        setConfirmedChronicleId(data.primary_chronicle.id);
        setUseCreate(false);
      } else if (data.create_chronicle) {
        setCreateForm({
          title: data.create_chronicle.title || '',
          type: data.create_chronicle.type || 'relational',
          subject: data.create_chronicle.subject || '',
        });
        setUseCreate(true);
      }
      // Pre-populate confirmed splits where the suggested Chronicle exists
      const autoSplits = {};
      for (const s of (data.split_suggestions || [])) {
        const match = chronicles.find(c => c.title === s.suggested_chronicle_title);
        if (match) autoSplits[s.entry_index] = match.id;
      }
      setSplitAssignments(autoSplits);
      setStep('review');
    } catch { feedback.fail(); }
    finally { setLoading(false); }
  };

  // ── Phase 2: Werner confirms → single DB write ─────────────────────────────
  const handleApply = async () => {
    const primaryId = useCreate ? null : confirmedChronicleId;
    if (!primaryId && !createForm.title.trim()) return;
    const splits = Object.entries(splitAssignments)
      .filter(([idx]) => !dismissedSplits.has(Number(idx)))
      .map(([idx, chronicle_id]) => ({ entry_index: Number(idx), chronicle_id }));
    setLoading(true);
    try {
      const { data } = await api.post('/chronicle/import/apply', {
        import_batch_id: proposal.import_batch_id,
        source_label: sourceLabel,
        import_mode: importMode,
        source_type: proposal.source_type,
        primary_chronicle_id: primaryId,
        create_chronicle: useCreate ? createForm : null,
        entries: proposal.entries,
        split_assignments: splits,
      });
      feedback.confirm();
      const targetId = data.chronicle_id;
      reset();
      onDone(targetId);
      if (useCreate) onChronicleCreated?.({ id: targetId, ...createForm, entry_count: data.inserted });
    } catch { feedback.fail(); }
    finally { setLoading(false); }
  };

  const resolvedTitle = useCreate
    ? (createForm.title || 'New Chronicle')
    : (chronicles.find(c => c.id === confirmedChronicleId)?.title || '—');
  const canApply = useCreate ? !!createForm.title.trim() : !!confirmedChronicleId;

  // ── Collapsed bar ──────────────────────────────────────────────────────────
  if (!open) {
    return (
      <div className={`fixed bottom-[72px] md:bottom-8 left-0 ${hasSidebar ? 'md:left-72' : ''} right-0 z-30 pointer-events-none flex justify-center`}>
        <div className="w-[70%] max-w-xl pointer-events-auto">
          <button onClick={handleOpen} style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(255,255,255,0.05)', border: `2px solid ${CHRONICLE_COLOR}cc`,
            borderRadius: 14, padding: '10px 16px', cursor: 'pointer',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
          }}>
            <span style={{ flex: 1, fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 13, color: '#5A5A5A', textAlign: 'left' }}>
              Import into a Chronicle…
            </span>
            <Send size={15} style={{ color: CHRONICLE_COLOR, flexShrink: 0 }} />
          </button>
        </div>
      </div>
    );
  }

  // ── Expanded ───────────────────────────────────────────────────────────────
  return (
    <>
      <div onClick={reset} style={{ position: 'fixed', inset: 0, zIndex: 55 }} />
      <div ref={containerRef} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 60, padding: '0 0 8px' }} onClick={e => e.stopPropagation()}>
        <div style={{ width: step === 'review' ? '88%' : '70%', maxWidth: step === 'review' ? 680 : 560, margin: '0 auto', transition: 'max-width 0.2s' }}>
          <div style={{ background: 'rgba(255,255,255,0.05)', border: `2px solid ${CHRONICLE_COLOR}cc`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 0 40px rgba(0,0,0,0.6)', maxHeight: '80vh', overflowY: 'auto' }}>

            {/* ── Header row ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px 6px', borderBottom: '0.5px solid #252525', position: 'sticky', top: 0, background: 'rgba(255,255,255,0.05)', zIndex: 1 }}>
              {step === 'review' && (
                <button onClick={() => setStep('form')} style={{ color: '#6b7084', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontSize: 10, marginRight: 4 }}>← back</button>
              )}
              {['append', 'replace'].map(m => (
                <button key={m} onClick={() => setImportMode(m)} style={{
                  background: importMode === m ? 'rgba(255,255,255,0.05)' : 'transparent',
                  color: importMode === m ? '#C8C8C8' : '#5A5A5A',
                  border: `0.5px solid ${importMode === m ? '#5A5A5A' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: 20, padding: '3px 10px', fontSize: 10,
                  fontFamily: 'monospace', cursor: 'pointer', flexShrink: 0,
                }}>{m}</button>
              ))}
              <div style={{ flex: 1 }} />
              <button onClick={reset} style={{ color: '#6b7084', background: 'none', border: 'none', cursor: 'pointer' }}><X size={14} /></button>
            </div>

            {/* Source label — always visible */}
            <div style={{ padding: '6px 14px 4px', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
              <input
                value={sourceLabel}
                onChange={e => setSourceLabel(e.target.value)}
                placeholder="Source label (optional) — e.g. Sonalee WhatsApp, Bear — Private Life"
                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#b0b4c0', fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.04em' }}
              />
            </div>

            {/* ── FORM STEP ── */}
            {step === 'form' && (
              <div style={{ padding: '8px 14px 12px' }}>
                <textarea
                  ref={textareaRef}
                  value={rawText}
                  onChange={e => { setRawText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 220) + 'px'; }}
                  placeholder="Paste export text — Bear, WhatsApp, or Claude conversation. Source type is auto-detected."
                  style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#C8C8C8', fontFamily: 'monospace', fontSize: 11, lineHeight: 1.6, resize: 'none', minHeight: 80, maxHeight: 220 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084' }}>
                    {rawText ? `${rawText.length.toLocaleString()} chars` : ''}
                  </span>
                  <button onClick={handleAnalyse} disabled={loading} style={{ ...primaryBtnStyle, width: 'auto', padding: '6px 18px', fontSize: 10 }}>
                    {loading ? 'Analysing…' : 'Analyse →'}
                  </button>
                </div>
              </div>
            )}

            {/* ── REVIEW STEP ── */}
            {step === 'review' && proposal && (
              <div style={{ padding: '12px 16px 16px' }}>

                {/* Detection summary */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: CHRONICLE_COLOR, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {proposal.source_type}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#f0f0f5', fontWeight: 500 }}>
                    {proposal.entry_count} entries
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084' }}>
                    after segmentation
                  </span>
                </div>

                {/* ── Chronicle assignment block ── */}
                <div style={{ marginBottom: 16, padding: '12px 14px', background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)', borderLeft: `2px solid ${CHRONICLE_COLOR}`, borderRadius: 8 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>Chronicle</p>

                  {/* Existing Chronicle confirmed */}
                  {!useCreate && confirmedChronicleId && !pickingExisting && (() => {
                    const c = chronicles.find(x => x.id === confirmedChronicleId);
                    return (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <div>
                            <span style={{ fontFamily: 'Georgia, serif', fontSize: 15, color: '#f0f0f5' }}>{c?.title}</span>
                            <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084', marginLeft: 8 }}>{c?.type}</span>
                          </div>
                          <button onClick={() => { setConfirmedChronicleId(null); setPickingExisting(true); }} style={{ ...ghostBtnStyle, fontSize: 9, padding: '2px 8px' }}>change</button>
                        </div>
                        {proposal.primary_chronicle_reason && (
                          <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 11, color: '#6b7084', margin: 0 }}>
                            {proposal.primary_chronicle_reason}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Create new Chronicle form */}
                  {useCreate && !pickingExisting && (
                    <div>
                      <input
                        value={createForm.title}
                        onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="Title"
                        autoFocus
                        style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.06)', outline: 'none', color: '#f0f0f5', fontFamily: 'Georgia, serif', fontSize: 15, padding: '2px 0 6px', marginBottom: 10 }}
                      />
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                        {Object.entries(TYPE_LABELS).map(([t, label]) => (
                          <button key={t} onClick={() => setCreateForm(f => ({ ...f, type: t }))} style={{
                            background: createForm.type === t ? `${TYPE_COLORS[t]}22` : 'transparent',
                            color: createForm.type === t ? TYPE_COLORS[t] : '#6b7084',
                            border: `0.5px solid ${createForm.type === t ? `${TYPE_COLORS[t]}55` : 'rgba(255,255,255,0.06)'}`,
                            borderRadius: 20, padding: '2px 10px', fontSize: 10, fontFamily: 'monospace', cursor: 'pointer',
                          }}>{label}</button>
                        ))}
                      </div>
                      <input
                        value={createForm.subject}
                        onChange={e => setCreateForm(f => ({ ...f, subject: e.target.value }))}
                        placeholder="One line describing what this Chronicle holds"
                        style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.06)', outline: 'none', color: '#b0b4c0', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 12, padding: '2px 0 6px', marginBottom: 8 }}
                      />
                      {chronicles.length > 0 && (
                        <button onClick={() => { setUseCreate(false); setPickingExisting(true); }} style={{ ...ghostBtnStyle, fontSize: 9, padding: '2px 8px' }}>
                          Select existing instead
                        </button>
                      )}
                    </div>
                  )}

                  {/* Pick existing dropdown */}
                  {pickingExisting && (
                    <select
                      defaultValue=""
                      onChange={e => {
                        if (e.target.value === '__create__') { setUseCreate(true); setConfirmedChronicleId(null); setPickingExisting(false); }
                        else { setConfirmedChronicleId(e.target.value); setUseCreate(false); setPickingExisting(false); }
                      }}
                      style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 6, padding: '6px 8px', color: '#f0f0f5', fontFamily: 'Georgia, serif', fontSize: 13, outline: 'none' }}
                    >
                      <option value="" disabled>Select a Chronicle…</option>
                      {chronicles.map(c => <option key={c.id} value={c.id} style={{ background: 'rgba(255,255,255,0.05)' }}>{c.title}</option>)}
                      <option value="__create__" style={{ background: 'rgba(255,255,255,0.05)', color: CHRONICLE_COLOR }}>+ Create new Chronicle</option>
                    </select>
                  )}

                  {/* Nothing assigned yet — show action buttons */}
                  {!useCreate && !confirmedChronicleId && !pickingExisting && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => setUseCreate(true)} style={{ ...primaryBtnStyle, width: 'auto', padding: '5px 14px', fontSize: 10 }}>
                        Create Chronicle
                      </button>
                      {chronicles.length > 0 && (
                        <button onClick={() => setPickingExisting(true)} style={{ ...ghostBtnStyle, fontSize: 10 }}>Select existing</button>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Split suggestions ── */}
                {(proposal.split_suggestions || []).filter(s => !dismissedSplits.has(s.entry_index)).length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                      Split suggestions
                    </p>
                    {proposal.split_suggestions.filter(s => !dismissedSplits.has(s.entry_index)).map(s => {
                      const assignedId = splitAssignments[s.entry_index];
                      const assignedChron = chronicles.find(c => c.id === assignedId);
                      return (
                        <div key={s.entry_index} style={{ marginBottom: 8, padding: '10px 12px', background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)', borderLeft: '2px solid #454860', borderRadius: 8 }}>
                          <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 12, color: '#b0b4c0', lineHeight: 1.5, marginBottom: 8 }}>
                            {s.body_preview}
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084' }}>→</span>
                            <span style={{ fontFamily: 'monospace', fontSize: 10, color: assignedChron ? CHRONICLE_COLOR : '#6b7084' }}>
                              {assignedChron ? assignedChron.title : s.suggested_chronicle_title}
                            </span>
                            {!assignedChron && (
                              <button
                                onClick={() => {
                                  const match = chronicles.find(c => c.title === s.suggested_chronicle_title);
                                  if (match) setSplitAssignments(prev => ({ ...prev, [s.entry_index]: match.id }));
                                  else feedback.fail();
                                }}
                                style={{ ...ghostBtnStyle, fontSize: 9, padding: '2px 8px', color: CHRONICLE_COLOR, borderColor: `${CHRONICLE_COLOR}44` }}
                              >✓ confirm</button>
                            )}
                            <select
                              value={assignedId || ''}
                              onChange={e => {
                                if (e.target.value) setSplitAssignments(prev => ({ ...prev, [s.entry_index]: e.target.value }));
                                else { const n = { ...splitAssignments }; delete n[s.entry_index]; setSplitAssignments(n); }
                              }}
                              style={{ background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 4, padding: '2px 6px', color: '#b0b4c0', fontFamily: 'monospace', fontSize: 9, outline: 'none' }}
                            >
                              <option value="">reassign…</option>
                              {chronicles.map(c => <option key={c.id} value={c.id} style={{ background: 'rgba(255,255,255,0.05)' }}>{c.title}</option>)}
                            </select>
                            <button
                              onClick={() => setDismissedSplits(prev => new Set([...prev, s.entry_index]))}
                              style={{ ...ghostBtnStyle, fontSize: 9, padding: '2px 8px', marginLeft: 'auto' }}
                            >dismiss</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Entries preview ── */}
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                    First {proposal.entries_preview.length} of {proposal.entry_count} entries
                  </p>
                  {proposal.entries_preview.map((entry, i) => {
                    const hasRewrite = entry.original_body && entry.original_body !== entry.body;
                    const displayBody = (hasRewrite && showOriginal[i]) ? entry.original_body : entry.body;
                    return (
                      <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084', letterSpacing: '0.08em' }}>
                            {formatEntryDate(entry.date)}
                          </span>
                          {hasRewrite && (
                            <button
                              onClick={() => toggleShowOriginal(i)}
                              style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7084', letterSpacing: '0.08em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              {showOriginal[i] ? 'show cleaned' : 'show original'}
                            </button>
                          )}
                        </div>
                        <p style={{ fontFamily: 'Georgia, serif', fontSize: 13, color: '#C8C8C8', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>
                          {displayBody.length > 280 ? displayBody.slice(0, 280) + '…' : displayBody}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* ── Confirm button ── */}
                <button
                  onClick={handleApply}
                  disabled={loading || !canApply}
                  style={{ ...primaryBtnStyle, opacity: canApply ? 1 : 0.35 }}
                >
                  {loading
                    ? 'Importing…'
                    : canApply
                      ? `Import ${proposal.entry_count} entries → ${resolvedTitle}`
                      : 'Assign a Chronicle above to import'}
                </button>

              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function Chronicle() {
  const [chronicles, setChronicles]         = useState([]);
  const [stats, setStats]                   = useState({ total_chronicles: 0, total_entries: 0 });
  const [loading, setLoading]               = useState(true);
  const [selectedChronicle, setSelectedChronicle] = useState(null);
  const [entries, setEntries]               = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [collapsedBatches, setCollapsedBatches] = useState({});
  const [newOpen, setNewOpen]               = useState(false);
  const [addEntryOpen, setAddEntryOpen]     = useState(false);

  const hasSidebar = window.innerWidth >= 768;

  useEffect(() => { loadChronicles(); loadStats(); }, []);

  const loadChronicles = async () => {
    try {
      const { data } = await api.get('/chronicle/chronicles');
      setChronicles(data);
      return data;
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/chronicle/stats');
      setStats(data);
    } catch { /* silent */ }
  };

  const openChronicle = async (c) => {
    setSelectedChronicle(c);
    setEntriesLoading(true);
    setEntries([]);
    try {
      const { data } = await api.get(`/chronicle/chronicles/${c.id}/entries?limit=500`);
      setEntries(data);
    } catch { /* silent */ }
    finally { setEntriesLoading(false); }
  };

  const handleEntryAdded = (entry) => {
    setEntries(prev => [...prev, entry]);
    setSelectedChronicle(c => c ? { ...c, entry_count: (c.entry_count || 0) + 1 } : c);
  };

  // Called when import completes. `chronicleId` is the Chronicle imported into.
  const handleImportDone = async (chronicleId) => {
    await loadChronicles();
    loadStats();
    if (chronicleId) {
      if (selectedChronicle?.id === chronicleId) {
        // Already viewing it — reload entries
        const { data } = await api.get(`/chronicle/chronicles/${chronicleId}/entries?limit=500`);
        setEntries(data);
      } else {
        // Navigate into the Chronicle that was just imported into
        const { data: all } = await api.get('/chronicle/chronicles');
        setChronicles(all);
        const target = all.find(c => c.id === chronicleId);
        if (target) openChronicle(target);
      }
    }
  };

  // Called when a new chronicle is created inline during import
  const handleChronicleCreated = (newChronicle) => {
    setChronicles(prev => {
      if (prev.find(c => c.id === newChronicle.id)) return prev;
      return [...prev, { ...newChronicle, entry_count: 0 }];
    });
    loadStats();
  };

  const toggleBatch = (batchId) => {
    setCollapsedBatches(prev => ({ ...prev, [batchId]: !prev[batchId] }));
  };

  const groupedEntries = (() => {
    const groups = [];
    let currentBatch = null;
    let currentGroup = null;
    for (const entry of entries) {
      const batchKey = entry.import_batch_id || '__manual__';
      if (batchKey !== currentBatch) {
        if (currentGroup) groups.push(currentGroup);
        currentBatch = batchKey;
        currentGroup = { batchId: batchKey, source: entry.source, entries: [entry] };
      } else {
        currentGroup.entries.push(entry);
      }
    }
    if (currentGroup) groups.push(currentGroup);
    return groups;
  })();

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div style={statPillStyle}>{stats.total_chronicles} chronicles</div>
        <div style={statPillStyle}>{stats.total_entries.toLocaleString()} entries</div>
      </div>

      {/* ── Chronicle List ────────────────────────────────────────────────────── */}
      {!selectedChronicle && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <button onClick={() => setNewOpen(true)} style={ghostBtnStyle}>
              <Plus size={12} /> New Chronicle
            </button>
          </div>

          <ZLoadingBar accent="#C04040" loading={loading} />
          {!loading && chronicles.length === 0 && <ZEmptyState mark="C" accent="#C04040" message="No chronicles yet — paste an import below to get started." />}

          {chronicles.map(c => {
            const typeColor = TYPE_COLORS[c.type] || '#b0b4c0';
            return (
              <div
                key={c.id}
                onClick={() => openChronicle(c)}
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.06)',
                  borderLeft: `2px solid ${CHRONICLE_COLOR}`,
                  borderRadius: 10, padding: '14px 16px', marginBottom: 8, cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontFamily: 'Georgia, serif', fontSize: 15, color: '#f0f0f5', flex: 1 }}>
                    {c.title}
                  </span>
                  <span style={{
                    background: `${typeColor}1A`, color: typeColor,
                    border: `0.5px solid ${typeColor}44`,
                    fontSize: 9, padding: '2px 8px', borderRadius: 4,
                    fontFamily: 'monospace', letterSpacing: '0.06em', flexShrink: 0,
                  }}>
                    {TYPE_LABELS[c.type]}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  {c.subject && (
                    <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 12, color: '#b0b4c0' }}>
                      {c.subject}
                    </span>
                  )}
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#b0b4c0', marginLeft: 'auto' }}>
                    {(c.entry_count || 0).toLocaleString()} entries
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Entries View ──────────────────────────────────────────────────────── */}
      {selectedChronicle && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ height: 32, display: 'flex', alignItems: 'center' }}>
              <Breadcrumb>
                <BreadcrumbList style={{ gap: 0 }}>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <button onClick={() => { setSelectedChronicle(null); setEntries([]); }} style={{ color: '#6b7084', fontSize: 13, fontWeight: 400, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
                        onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}>
                        Chronicle
                      </button>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
                  <BreadcrumbItem>
                    <BreadcrumbPage style={{ color: '#f0f0f5', fontSize: 13, fontWeight: 400 }}>
                      {selectedChronicle.title}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <button
              onClick={() => setAddEntryOpen(true)}
              style={{ ...ghostBtnStyle, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <Plus size={11} /> Entry
            </button>
          </div>

          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 400, color: '#f0f0f5', marginBottom: 4 }}>
              {selectedChronicle.title}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {selectedChronicle.subject && (
                <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 12, color: '#b0b4c0' }}>
                  {selectedChronicle.subject}
                </span>
              )}
              <span style={{
                background: `${TYPE_COLORS[selectedChronicle.type]}1A`,
                color: TYPE_COLORS[selectedChronicle.type],
                border: `0.5px solid ${TYPE_COLORS[selectedChronicle.type]}44`,
                fontSize: 9, padding: '2px 8px', borderRadius: 4,
                fontFamily: 'monospace', letterSpacing: '0.06em',
              }}>
                {TYPE_LABELS[selectedChronicle.type]}
              </span>
              <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#b0b4c0', marginLeft: 'auto' }}>
                {entries.length.toLocaleString()} entries
              </span>
            </div>
          </div>

          <ZLoadingBar accent="#C04040" loading={entriesLoading} />
          {!entriesLoading && entries.length === 0 && <ZEmptyState mark="C" accent="#C04040" message="No entries yet — paste an import below or add one manually." />}

          {groupedEntries.map((group, gi) => {
            const isImportBatch = group.batchId !== '__manual__';
            const isCollapsed = collapsedBatches[group.batchId];
            return (
              <div key={group.batchId + gi}>
                {isImportBatch && (
                  <div
                    onClick={() => toggleBatch(group.batchId)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 14px', cursor: 'pointer' }}
                  >
                    <div style={{ height: '0.5px', flex: 1, background: 'rgba(255,255,255,0.05)' }} />
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#b0b4c0', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                      {group.entries.length} entries · {group.source}
                      {isCollapsed ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
                    </span>
                    <div style={{ height: '0.5px', flex: 1, background: 'rgba(255,255,255,0.05)' }} />
                  </div>
                )}

                {!isCollapsed && group.entries.map((entry) => (
                  <div key={entry.id} style={{ marginBottom: 28, paddingBottom: 28, borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#b0b4c0', letterSpacing: '0.08em', marginBottom: 8 }}>
                      {formatEntryDate(entry.date)}
                      {entry.context && (
                        <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#b0b4c0', marginLeft: 10 }}>
                          {entry.context}
                        </span>
                      )}
                    </div>
                    <p style={{ fontFamily: 'Georgia, serif', fontSize: 14, color: '#D8D8D8', lineHeight: 1.85, whiteSpace: 'pre-wrap', margin: 0 }}>
                      {renderBody(entry.body)}
                    </p>
                    {entry.linked_chronicles?.length > 0 && (
                      <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#b0b4c0', marginTop: 10 }}>
                        Also in: {entry.linked_chronicles.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────────── */}
      {newOpen && (
        <NewChronicleModal
          onClose={() => setNewOpen(false)}
          onCreated={(c) => { setChronicles(prev => [...prev, c]); loadStats(); }}
        />
      )}

      {addEntryOpen && selectedChronicle && (
        <AddEntrySheet
          chronicle={selectedChronicle}
          onClose={() => setAddEntryOpen(false)}
          onAdded={handleEntryAdded}
        />
      )}

      {/* ── Import bar — always visible on Chronicle tab ─────────────────────── */}
      {!addEntryOpen && (
        <ImportBar
          chronicles={chronicles}
          onDone={handleImportDone}
          onChronicleCreated={handleChronicleCreated}
          hasSidebar={hasSidebar}
        />
      )}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────

const statPillStyle = {
  background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.06)',
  borderRadius: 20, padding: '4px 12px',
  fontSize: 11, color: '#b0b4c0', fontFamily: 'monospace',
};

const labelStyle = {
  fontFamily: 'monospace', fontSize: 9,
  letterSpacing: '0.12em', textTransform: 'uppercase',
  color: '#6b7084', marginBottom: 6,
};

const inputStyle = {
  width: '100%', background: '#0a0a0f',
  border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 6,
  padding: '8px 10px', color: '#f0f0f5',
  fontSize: 13, fontFamily: 'Georgia, serif', outline: 'none',
};

const primaryBtnStyle = {
  width: '100%', background: CHRONICLE_COLOR,
  color: '#0a0a0f', border: 'none', borderRadius: 8,
  padding: '10px 16px', fontSize: 11, fontFamily: 'monospace',
  letterSpacing: '0.08em', textTransform: 'uppercase',
  cursor: 'pointer', fontWeight: 600,
};

const ghostBtnStyle = {
  background: 'transparent', color: '#b0b4c0',
  border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 6,
  padding: '5px 12px', fontSize: 11, fontFamily: 'monospace',
  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
};

